/**
 * Sortable rows used by /manage-content. Extracted from ManageContent.jsx
 * (Jun 4 2026) to shrink the page file and let the rows be reused if
 * needed elsewhere.
 *
 *   - SortableSeriesRow  — wraps a top-level series card with a drag handle.
 *   - SortableEpisodeRow — drag-sortable single-episode/chapter row with
 *                          position input, ↑/↓ arrows, cover/edit/delete.
 *
 * Both rows use @dnd-kit/sortable's `useSortable` hook.
 */
import React from 'react';
import {
  GripVertical, Edit, Trash2, Image as ImageIcon, Sparkles, ArrowUp, ArrowDown,
} from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/**
 * @dnd-kit-powered wrapper for a top-level series row. The drag handle is
 * exposed only via the GripVertical icon (render-prop `listeners`), so the
 * row's interactive children — bulk-select, edit modal, episode list — all
 * stay tappable without fighting the drag. Mirrors the SortableEpisodeRow
 * pattern below.
 */
export const SortableSeriesRow = ({ id, selected, children }) => {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-slate-800/50 rounded-lg border transition-colors ${
        isDragging
          ? 'border-indigo-400 ring-2 ring-indigo-400/50 shadow-2xl shadow-indigo-500/30'
          : selected
          ? 'border-amber-400/70 ring-1 ring-amber-400/40'
          : 'border-slate-700/50'
      }`}
      data-testid={`series-row-${id}`}
      {...attributes}
    >
      {typeof children === 'function' ? children({ listeners }) : children}
    </div>
  );
};

/**
 * @dnd-kit-powered row. `useSortable` wires up the drag listeners, the
 * transform CSS that visually follows the pointer, and the drop-target
 * collision tracking. We expose only the GripVertical icon as the drag
 * activator (via {...listeners}), so all the interactive children (the
 * position input, the ↑/↓ arrows, the episode body) stay tappable — no
 * more "drag swallows my click" issues.
 *
 * Touch targets are sized 44 × 28 (input) and 44 × 28 (arrow buttons) to
 * meet the WCAG 2.5.5 / Apple HIG minimum tap target on mobile while
 * keeping the row visually compact.
 */
export const SortableEpisodeRow = ({
  ep,
  i,
  item,
  num,
  xp,
  onPositionChange,
  onMoveBy,
  onEditEpisode,
  onEditEpisodeCover,
  onDeleteEpisode,
}) => {
  const id = ep.episode_id || ep.chapter_id || String(i);
  const total = item.episodes?.length || 0;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center gap-2 sm:gap-3 p-3 bg-slate-800/50 rounded-lg border ${
        isDragging
          ? 'border-indigo-400 ring-2 ring-indigo-400/50 shadow-2xl shadow-indigo-500/30 bg-slate-800'
          : 'border-transparent hover:border-slate-700'
      } transition-colors touch-none cursor-grab active:cursor-grabbing select-none`}
      data-testid={`episode-row-${id}`}
      {...attributes}
      {...listeners}
    >
      {/* Visual grip cue only — the whole row is the drag activator
          (set via {...listeners} on the outer div), so users on PC can
          grab title, file size, or empty space alike. */}
      <div className="shrink-0 w-7 h-9 flex items-center justify-center text-gray-500 group-hover:text-indigo-300 pointer-events-none">
        <GripVertical className="w-5 h-5" strokeWidth={2.2} />
      </div>

      {/* Position column */}
      <div className="flex flex-col items-center shrink-0 w-12 sm:w-14">
        <input
          type="number"
          inputMode="numeric"
          enterKeyHint="go"
          min={1}
          max={total}
          defaultValue={i + 1}
          key={`${id}-${i + 1}`}
          onClick={(e) => {
            e.stopPropagation();
            e.currentTarget.select();
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.currentTarget.blur();
            }
          }}
          onBlur={(e) => onPositionChange(item, i, e.target.value)}
          className="w-full h-10 text-center text-base font-extrabold tabular-nums text-indigo-200 bg-indigo-500/15 border-2 border-indigo-400/40 rounded-lg focus:border-indigo-300 focus:bg-indigo-500/25 focus:outline-none focus:ring-2 focus:ring-indigo-400/60 hover:border-indigo-400/70 transition-colors cursor-text"
          data-testid={`episode-position-input-${id}`}
          title="Type a number to jump to that position, then press Enter"
          aria-label={`Position of ${ep.title}. Type a new number to move.`}
        />
        <span className="text-[9px] text-indigo-400/60 uppercase tracking-wider mt-0.5 leading-none">type #</span>
      </div>

      {/* ↑/↓ arrows */}
      <div className="flex flex-col gap-1 shrink-0">
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onMoveBy(item, i, -1); }}
          disabled={i === 0}
          className="w-9 h-7 sm:w-8 sm:h-6 rounded-md bg-slate-900/70 border border-slate-700 hover:border-indigo-400 hover:text-indigo-200 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-gray-300 transition-colors"
          data-testid={`episode-move-up-${id}`}
          aria-label="Move episode up"
          title="Move up"
        >
          <ArrowUp className="w-4 h-4" strokeWidth={3} />
        </button>
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onMoveBy(item, i, 1); }}
          disabled={i === total - 1}
          className="w-9 h-7 sm:w-8 sm:h-6 rounded-md bg-slate-900/70 border border-slate-700 hover:border-indigo-400 hover:text-indigo-200 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-gray-300 transition-colors"
          data-testid={`episode-move-down-${id}`}
          aria-label="Move episode down"
          title="Move down"
        >
          <ArrowDown className="w-4 h-4" strokeWidth={3} />
        </button>
      </div>

      <div className="flex-1 min-w-0">
        <div className="font-medium truncate text-sm sm:text-base">{ep.title}</div>
        <div className="text-xs text-gray-400 flex items-center gap-1.5 flex-wrap">
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              const t = item.episodes?.length || 0;
              const next = window.prompt(
                `Move "${ep.title}" to which position? (1 – ${t})`,
                String(i + 1),
              );
              if (next != null && next !== '' && next !== String(i + 1)) {
                onPositionChange(item, i, next);
              }
            }}
            className="px-1.5 py-0.5 rounded bg-indigo-500/15 border border-indigo-400/30 text-indigo-200 font-bold hover:bg-indigo-500/25 hover:border-indigo-300 active:bg-indigo-500/35 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-400/60"
            data-testid={`episode-inline-position-${id}`}
            title="Tap to change position"
            aria-label={`Episode position ${num}. Tap to move.`}
          >
            #{num}
          </button>
          <span>·</span>
          <span>{((ep.file_size || 0) / 1024 / 1024).toFixed(1)} MB</span>
          {xp != null && (
            <>
              <span>·</span>
              <span
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-400/40 text-amber-200 font-bold tabular-nums"
                title="XP rewarded for this upload — visible to the writer and admins only"
                data-testid={`episode-xp-${ep.episode_id || ep.chapter_id}`}
              >
                <Sparkles className="w-3 h-3" /> {xp.toLocaleString()} XP
              </span>
            </>
          )}
        </div>
      </div>

      {/* Per-episode actions: edit cover + edit title + delete. */}
      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onEditEpisodeCover?.(item, ep); }}
          className="w-9 h-9 rounded-md text-gray-400 hover:text-fuchsia-200 hover:bg-fuchsia-500/15 active:bg-fuchsia-500/25 flex items-center justify-center transition-colors"
          data-testid={`episode-edit-cover-${id}`}
          aria-label={`Change cover for ${ep.title}`}
          title="Change episode cover"
        >
          <ImageIcon className="w-4 h-4" />
        </button>
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onEditEpisode?.(item, ep); }}
          className="w-9 h-9 rounded-md text-gray-400 hover:text-indigo-200 hover:bg-indigo-500/15 active:bg-indigo-500/25 flex items-center justify-center transition-colors"
          data-testid={`episode-edit-${id}`}
          aria-label={`Edit ${ep.title}`}
          title="Rename episode"
        >
          <Edit className="w-4 h-4" />
        </button>
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onDeleteEpisode?.(item, ep); }}
          className="w-9 h-9 rounded-md text-gray-400 hover:text-red-300 hover:bg-red-500/15 active:bg-red-500/25 flex items-center justify-center transition-colors"
          data-testid={`episode-delete-${id}`}
          aria-label={`Delete ${ep.title}`}
          title="Delete episode"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
